import React, { useState, useRef, useEffect } from 'react';
import { trackPageView } from '../../utlis/useAnalytics';
import { PageName } from '../../configs/config';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import html2canvas from 'html2canvas-pro';
import { Helmet } from "react-helmet-async";
import jsPDF from 'jspdf';
import {
  Trash2,
  Plus,
  Download,
  Upload,
  Check,
  Mail,
  Phone,
  Globe,
  DollarSign,
  Calendar,
  Hash,
} from 'lucide-react';

// --- Type definitions ---
interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface BankDetails {
  bankName: string;
  accountNumber: string;
  routingNumber: string;
  swiftCode: string;
  accountName: string;
  branchName: string;
  additionalInfo: string;
}

interface InvoiceData {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  currency: string;
  logoUrl: string | null;
  companyName: string;
  companyAddress: string;
  companyEmail: string;
  companyPhone: string;
  companyWebsite: string;
  clientName: string;
  clientAddress: string;
  clientEmail: string;
  clientPhone: string;
  items: InvoiceItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discount: number;
  total: number;
  notes: string;
  terms: string;
  bankDetails: BankDetails;
}

// --- Default data and formatter ---
const defaultInvoiceData: InvoiceData = {
  invoiceNumber: `INV-${format(new Date(), 'yyyyMMdd')}-${Math.floor(1000 + Math.random() * 9000)}`,
  date: format(new Date(), 'yyyy-MM-dd'),
  dueDate: format(new Date(Date.now() + 30 * 24 * 3600 * 1000), 'yyyy-MM-dd'),
  currency: 'USD',
  logoUrl: null,
  companyName: 'Global Account Services',
  companyAddress: '1234 Business Avenue, Suite 567, New York, NY 10001',
  companyEmail: 'billing@globalaccounts.com',
  companyPhone: '+1 (555) 123-4567',
  companyWebsite: 'www.globalaccounts.com',
  clientName: 'Client Company Name',
  clientAddress: 'Client Street Address, City, State, ZIP',
  clientEmail: 'client@example.com',
  clientPhone: '+1 (555) 987-6543',
  items: [
    {
      id: uuidv4(),
      description: 'Professional Services',
      quantity: 1,
      rate: 100,
      amount: 100,
    },
  ],
  taxRate: 10,
  discount: 0,
  // ✅ Always calculated dynamically:
  subtotal: 0,
  taxAmount: 0,
  total: 0,
  notes: 'Thank you for your business!',
  terms:
    'Payment is due within 30 days from the date of invoice. Late payments are subject to a 2% monthly fee.',
  bankDetails: {
    bankName: 'Global Bank',
    accountNumber: '123456789',
    routingNumber: '987654321',
    swiftCode: 'GLBANK123',
    accountName: 'Global Account Services Ltd.',
    branchName: 'New York Financial District',
    additionalInfo: '',
  },
};


const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);

// --- Main component ---
const InvoiceGenerator: React.FC = () => {
  // Analytics
  useEffect(() => {
    trackPageView(PageName.InvoiceGenerator);
  }, []);

  const [invoiceData, setInvoiceData] = useState<InvoiceData>(defaultInvoiceData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const invoiceRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Recalculate totals whenever items, taxRate, or discount change
useEffect(() => {
  const subtotal = invoiceData.items.reduce((sum, i) => sum + i.amount, 0);
  const taxAmount = (subtotal * invoiceData.taxRate) / 100;
  const total = subtotal + taxAmount - invoiceData.discount;

  setInvoiceData((d) => ({
    ...d,
    subtotal,
    taxAmount,
    total,
  }));
}, [invoiceData.items, invoiceData.taxRate, invoiceData.discount]);


  // Handlers
  const handleItemChange = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setInvoiceData((d) => {
      const items = d.items.map((i) => {
        if (i.id === id) {
          const upd = { ...i, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            upd.amount = Number(upd.quantity) * Number(upd.rate);
          }
          return upd;
        }
        return i;
      });
      return { ...d, items };
    });
  };

  const addItem = () =>
    setInvoiceData((d) => ({
      ...d,
      items: [
        ...d.items,
        { id: uuidv4(), description: '', quantity: 1, rate: 0, amount: 0 },
      ],
    }));

  const removeItem = (id: string) =>
    setInvoiceData((d) => ({ ...d, items: d.items.filter((i) => i.id !== id) }));

  const handleChange = (f: keyof InvoiceData, v: string | number) =>
    setInvoiceData((d) => ({ ...d, [f]: v }));

  const handleBankChange = (f: keyof BankDetails, v: string) =>
    setInvoiceData((d) => ({
      ...d,
      bankDetails: { ...d.bankDetails, [f]: v },
    }));

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setInvoiceData((d) => ({ ...d, logoUrl: reader.result as string }));
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setInvoiceData((d) => ({ ...d, logoUrl: null }));
  const toggleEdit = () => {
    setIsEditing((e) => !e);
    setIsSaved(false);
  };
  const saveInvoice = () => {
    setIsSaved(true);
    setIsEditing(false);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const generatePDF = async () => {
    if (!invoiceRef.current) return;
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const w = 210;
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, 'PNG', 0, 0, w, h);
      pdf.save(`invoice-${invoiceData.invoiceNumber}.pdf`);
    } catch {
      alert('Error generating PDF.');
    }
  };

  // Common input‐style classes
  const textInputClass = 'border border-gray-300 rounded-md px-3 py-2 bg-gray-100 w-full';
  const numericInputClass = textInputClass + ' text-right';

  return (
    <>
      <Helmet>
        <title>Free Invoice Generator – Create GST Invoices Instantly | EximPe</title>
        <meta
          name="description"
          content="Generate professional export and import invoices in minutes with EximPe’s free online Invoice Generator. Download, print, or share instantly."
        />
      </Helmet>

      <div className="pt-24 pb-10 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header & Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  Invoice Generator
                </h1>
                <p className="text-gray-600 mt-1">
                  Create, edit and download professional invoices with PDF support
                </p>
              </div>
              <div className="flex gap-2">
                {isEditing ? (
                  <button
                    onClick={saveInvoice}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:cursor-pointer"
                  >
                    <Check size={18} className="mr-1" />
                    Save
                  </button>
                ) : (
                  <button
                    onClick={toggleEdit}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:cursor-pointer"
                  >
                    {isSaved ? (
                      <>
                        <Check size={18} className="mr-1" />
                        Saved
                      </>
                    ) : (
                      <>Edit</>
                    )}
                  </button>
                )}
                <button
                  onClick={generatePDF}
                  className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:cursor-pointer"
                >
                  <Download size={18} className="mr-1" />
                  Download PDF
                </button>
              </div>
            </div>

            {/* Invoice Panel */}
            <div className="bg-white rounded-xl shadow-xl p-6" ref={invoiceRef}>
              {/* ---------------------------------------------------- */}
              {/* Logo & Company Info  (Left)   |   Invoice Details (Right) */}
              {/* ---------------------------------------------------- */}
              <div className="flex flex-col md:flex-row justify-between mb-8 gap-6">
                {/* Left column: Logo & Company Info */}
                <div className="w-full md:w-1/2 flex flex-col space-y-3">
                  {isEditing ? (
                    <>
                      {/* Logo Upload + Remove */}
                      <div className="flex items-center gap-3">
                        {invoiceData.logoUrl ? (
                          <div className="relative w-48 h-24">
                            <img
                              src={invoiceData.logoUrl!}
                              alt="Logo"
                              className="object-contain w-full h-full"
                            />
                            <button
                              onClick={removeLogo}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="hover:cursor-pointer flex items-center justify-center w-48 h-24 border-2 border-dashed border-gray-300 rounded-md"
                          >
                            <Upload size={24} className="mr-2 text-gray-400" />
                            <span className="text-gray-500">Upload Logo</span>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleLogoUpload}
                              accept="image/*"
                              className="hidden"
                            />
                          </button>
                        )}
                      </div>

                      {/* Company Name */}
                      <input
                        type="text"
                        value={invoiceData.companyName}
                        onChange={(e) => handleChange('companyName', e.target.value)}
                        className={textInputClass}
                        placeholder="Company Name"
                      />

                      {/* Company Address */}
                      <textarea
                        value={invoiceData.companyAddress}
                        onChange={(e) => handleChange('companyAddress', e.target.value)}
                        className={textInputClass}
                        rows={2}
                        placeholder="Company Address"
                      />

                      {/* Company Email */}
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-gray-500" />
                        <input
                          type="text"
                          value={invoiceData.companyEmail}
                          onChange={(e) => handleChange('companyEmail', e.target.value)}
                          className={textInputClass}
                          placeholder="Company Email"
                        />
                      </div>

                      {/* Company Phone */}
                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-gray-500" />
                        <input
                          type="text"
                          value={invoiceData.companyPhone}
                          onChange={(e) => handleChange('companyPhone', e.target.value)}
                          className={textInputClass}
                          placeholder="Company Phone"
                        />
                      </div>

                      {/* Company Website */}
                      <div className="flex items-center gap-2">
                        <Globe size={16} className="text-gray-500" />
                        <input
                          type="text"
                          value={invoiceData.companyWebsite}
                          onChange={(e) => handleChange('companyWebsite', e.target.value)}
                          className={textInputClass}
                          placeholder="Company Website"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* View mode: show logo if exists */}
                      {invoiceData.logoUrl && (
                        <div className="mb-4 w-48 h-24">
                          <img
                            src={invoiceData.logoUrl}
                            alt="Logo"
                            className="object-contain w-full h-full"
                          />
                        </div>
                      )}
                      <p className="font-bold text-lg">{invoiceData.companyName}</p>
                      <p className="text-sm mt-1 whitespace-pre-line">
                        {invoiceData.companyAddress}
                      </p>
                      <div className="flex items-center mt-2 text-sm text-gray-600">
                        <Mail size={14} className="mr-1 text-gray-500" />
                        {invoiceData.companyEmail}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone size={14} className="mr-1 text-gray-500" />
                        {invoiceData.companyPhone}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Globe size={14} className="mr-1 text-gray-500" />
                        {invoiceData.companyWebsite}
                      </div>
                    </>
                  )}
                </div>

                {/* Right column: Invoice Details */}
                <div className="w-full md:w-1/2 text-right space-y-2">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">INVOICE</h2>
                  {isEditing ? (
                    <>
                      {/* Invoice # */}
                      <div className="flex items-center gap-2 justify-end">
                        <Hash size={14} className="text-gray-600" />
                        <input
                          type="text"
                          value={invoiceData.invoiceNumber}
                          onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                          className={`${textInputClass} w-40 text-right`}
                          placeholder="Invoice #"
                        />
                      </div>

                      {/* Invoice Date */}
                      <div className="flex items-center gap-2 justify-end">
                        <Calendar size={14} className="text-gray-600" />
                        <input
                          type="date"
                          value={invoiceData.date}
                          onChange={(e) => handleChange('date', e.target.value)}
                          className={`${textInputClass} w-40 text-right`}
                        />
                      </div>

                      {/* Due Date */}
                      <div className="flex items-center gap-2 justify-end">
                        <Calendar size={14} className="text-gray-600" />
                        <input
                          type="date"
                          value={invoiceData.dueDate}
                          onChange={(e) => handleChange('dueDate', e.target.value)}
                          className={`${textInputClass} w-40 text-right`}
                        />
                      </div>

                      {/* Currency Selector */}
                      <div className="flex items-center gap-2 justify-end">
                        <DollarSign size={14} className="text-gray-600" />
                        <select
                          value={invoiceData.currency}
                          onChange={(e) => handleChange('currency', e.target.value)}
                          className={`${textInputClass} w-40 text-right`}
                        >
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                          <option value="INR">INR - Indian Rupee</option>
                          <option value="JPY">JPY - Japanese Yen</option>
                          <option value="AUD">AUD - Australian Dollar</option>
                          <option value="CAD">CAD - Canadian Dollar</option>
                          <option value="SGD">SGD - Singapore Dollar</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <Hash size={14} className="mr-1 text-gray-500" />
                          <span className="font-medium">Invoice #:</span>
                        </div>
                        <span>{invoiceData.invoiceNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1 text-gray-500" />
                          <span className="font-medium">Date:</span>
                        </div>
                        <span>
                          {format(new Date(invoiceData.date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1 text-gray-500" />
                          <span className="font-medium">Due:</span>
                        </div>
                        <span>
                          {format(new Date(invoiceData.dueDate), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <DollarSign size={14} className="mr-1 text-gray-500" />
                          <span className="font-medium">Currency:</span>
                        </div>
                        <span>{invoiceData.currency}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ------------------ */}
              {/* Bill To Section */}
              {/* ------------------ */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
                  Bill To:
                </h3>

                {isEditing ? (
                  <div className="w-full md:w-1/2 space-y-3">
                    <input
                      type="text"
                      value={invoiceData.clientName}
                      onChange={(e) => handleChange('clientName', e.target.value)}
                      className={textInputClass}
                      placeholder="Client Name"
                    />

                    <textarea
                      value={invoiceData.clientAddress}
                      onChange={(e) => handleChange('clientAddress', e.target.value)}
                      className={textInputClass}
                      rows={2}
                      placeholder="Client Address"
                    />

                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-gray-500" />
                      <input
                        type="text"
                        value={invoiceData.clientEmail}
                        onChange={(e) => handleChange('clientEmail', e.target.value)}
                        className={textInputClass}
                        placeholder="Client Email"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-gray-500" />
                      <input
                        type="text"
                        value={invoiceData.clientPhone}
                        onChange={(e) => handleChange('clientPhone', e.target.value)}
                        className={textInputClass}
                        placeholder="Client Phone"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-full md:w-1/2">
                    <p className="font-medium text-lg">{invoiceData.clientName}</p>
                    <p className="text-sm text-gray-600 mb-1 whitespace-pre-line">
                      {invoiceData.clientAddress}
                    </p>
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <Mail size={14} className="mr-1" />
                      {invoiceData.clientEmail}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone size={14} className="mr-1" />
                      {invoiceData.clientPhone}
                    </div>
                  </div>
                )}
              </div>

              {/* ------------------ */}
              {/* Invoice Items Table */}
              {/* ------------------ */}
              <div className="mb-8 overflow-x-auto">
                <h3 className="text-lg font-bold mb-2 border-b pb-1">Invoice Items:</h3>
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Rate</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      {isEditing && <th className="px-4 py-2 text-center">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, idx) => (
                      <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {isEditing ? (
                          <>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleItemChange(item.id, 'description', e.target.value)
                                }
                                className={textInputClass}
                                placeholder="Description"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(e) =>
                                  handleItemChange(item.id, 'quantity', Number(e.target.value))
                                }
                                className={numericInputClass}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                value={item.rate}
                                onChange={(e) =>
                                  handleItemChange(item.id, 'rate', Number(e.target.value))
                                }
                                className={numericInputClass}
                              />
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span>{formatCurrency(item.amount, invoiceData.currency)}</span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button onClick={() => removeItem(item.id)}>
                                <Trash2 size={18} className="text-red-500" />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2">{item.description}</td>
                            <td className="px-4 py-2 text-right">{item.quantity}</td>
                            <td className="px-4 py-2 text-right">
                              {formatCurrency(item.rate, invoiceData.currency)}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {formatCurrency(item.amount, invoiceData.currency)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}

                    {isEditing && (
                      <tr>
                        <td colSpan={5} className="px-4 py-2">
                          <button
                            onClick={addItem}
                            className="flex items-center text-teal-600"
                          >
                            <Plus size={18} className="mr-1" />
                            Add Item
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ------------------------- */}
              {/* Invoice Summary (Totals) */}
              {/* ------------------------- */}
              <div className="mb-8 flex justify-end">
                <div className="w-full sm:w-1/2 lg:w-1/3">
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 font-medium">
                      Invoice Summary
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      {/* Subtotal */}
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-gray-600">Subtotal:</span>
                        <span></span>
                        <span className="text-right">
                          {formatCurrency(invoiceData.subtotal, invoiceData.currency)}
                        </span>
                      </div>
                      {/* Tax Rate / Amount */}
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-gray-600">Tax Rate:</span>
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={invoiceData.taxRate}
                            onChange={(e) =>
                              handleChange('taxRate', Number(e.target.value))
                            }
                            className={numericInputClass + ' w-20'}
                          />
                        ) : (
                          <span></span>
                        )}
                        <span className="text-right">
                          {formatCurrency(invoiceData.taxAmount, invoiceData.currency)}
                        </span>
                      </div>
                      {/* Discount */}
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-gray-600">Discount:</span>
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            value={invoiceData.discount}
                            onChange={(e) =>
                              handleChange('discount', Number(e.target.value))
                            }
                            className={numericInputClass + ' w-20'}
                          />
                        ) : (
                          <span></span>
                        )}
                        <span className="text-right">
                          {formatCurrency(invoiceData.discount, invoiceData.currency)}
                        </span>
                      </div>
                      {/* Total */}
                      <div className="grid grid-cols-3 items-center pt-2 border-t font-bold">
                        <span className="col-span-2">Total:</span>
                        <span className="text-right">
                          {formatCurrency(invoiceData.total, invoiceData.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ------------------------- */}
              {/* Notes & Terms */}
              {/* ------------------------- */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Notes</h3>
                  {isEditing ? (
                    <textarea
                      value={invoiceData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className={textInputClass}
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-600 whitespace-pre-line">
                      {invoiceData.notes}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    Terms & Conditions
                  </h3>
                  {isEditing ? (
                    <textarea
                      value={invoiceData.terms}
                      onChange={(e) => handleChange('terms', e.target.value)}
                      className={textInputClass}
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-600 whitespace-pre-line">
                      {invoiceData.terms}
                    </p>
                  )}
                </div>
              </div>

              {/* ------------------------- */}
              {/* Payment Information (Bank) */}
              {/* ------------------------- */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
                  Payment Information
                </h3>

                {isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Bank Name</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.bankName}
                        onChange={(e) => handleBankChange('bankName', e.target.value)}
                        className={textInputClass}
                      />

                      <label className="block text-sm font-medium">Account Name</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.accountName}
                        onChange={(e) => handleBankChange('accountName', e.target.value)}
                        className={textInputClass}
                      />

                      <label className="block text-sm font-medium">Account Number</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.accountNumber}
                        onChange={(e) =>
                          handleBankChange('accountNumber', e.target.value)
                        }
                        className={textInputClass}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Branch Name</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.branchName}
                        onChange={(e) => handleBankChange('branchName', e.target.value)}
                        className={textInputClass}
                      />

                      <label className="block text-sm font-medium">Routing Number</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.routingNumber}
                        onChange={(e) =>
                          handleBankChange('routingNumber', e.target.value)
                        }
                        className={textInputClass}
                      />

                      <label className="block text-sm font-medium">SWIFT Code</label>
                      <input
                        type="text"
                        value={invoiceData.bankDetails.swiftCode}
                        onChange={(e) => handleBankChange('swiftCode', e.target.value)}
                        className={textInputClass}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium">Additional Information</label>
                      <textarea
                        value={invoiceData.bankDetails.additionalInfo}
                        onChange={(e) =>
                          handleBankChange('additionalInfo', e.target.value)
                        }
                        className={textInputClass}
                        rows={2}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-600">
                    <div>
                      <p>
                        <span className="font-medium">Bank Name:</span>{' '}
                        {invoiceData.bankDetails.bankName}
                      </p>
                      <p>
                        <span className="font-medium">Account Name:</span>{' '}
                        {invoiceData.bankDetails.accountName}
                      </p>
                      <p>
                        <span className="font-medium">Account Number:</span>{' '}
                        {invoiceData.bankDetails.accountNumber}
                      </p>
                    </div>
                    <div>
                      <p>
                        <span className="font-medium">Branch Name:</span>{' '}
                        {invoiceData.bankDetails.branchName}
                      </p>
                      <p>
                        <span className="font-medium">Routing Number:</span>{' '}
                        {invoiceData.bankDetails.routingNumber}
                      </p>
                      <p>
                        <span className="font-medium">SWIFT Code:</span>{' '}
                        {invoiceData.bankDetails.swiftCode}
                      </p>
                    </div>
                    {invoiceData.bankDetails.additionalInfo && (
                      <div className="sm:col-span-2">
                        <p className="font-medium">Additional Information:</p>
                        <p className="whitespace-pre-line">
                          {invoiceData.bankDetails.additionalInfo}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* -------------------------------------- */}
            {/* SEO Content Placeholder (unchanged)  */}
            {/* -------------------------------------- */}
            <div className="mt-16 bg-white rounded-xl shadow-lg p-6 sm:p-8 prose max-w-none text-gray-700">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center lg:text-left md:text-left">
                Invoice Generation
              </h2>
              <p>
                We understand that sellers and buyers in Eximpe rely on invoices to ensure that trade
                with other nations runs smoothly. An export invoice is used to process payments and to
                make sure your goods cross borders easily, are paid promptly and follow the rules for
                international trade.
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                What is an Export Invoice?
              </h3>
              <p>
                An export invoice shows the specifics of the deal between the exporter and the buyer
                from another country. It provides details about the goods or products being traded and
                must be presented for clearing goods, paying and making insurance claims. Your invoice
                must be identical to other paperwork in shipping and payment to prevent any conflicts
                or delays.
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Key Components of an Export Invoice
              </h3>
              <p>
                To comply with international standards and ensure your shipments are processed without
                delay, your invoice should include:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  <strong>Exporter details:</strong> Information about exporters includes their names,
                  addresses, phone numbers and unique identifiers such as GSTIN, PAN and IEC Code
                  (only for Indian exporters).
                </li>
                <li>
                  <strong>Buyer (importer) details:</strong> You must provide the name, address and
                  tax ID of the buyer, since the importing country requires it by law.
                </li>
                <li>
                  <strong>Unique invoice number and date:</strong> An invoice number and date must be
                  included on all documents and should not be repeated for other transactions.
                </li>
                <li>
                  <strong>Description of goods or services:</strong> Every product should be described
                  by labeling it with its name, parameters, HSN/SAC code, quantity, unit cost and
                  total amount.
                </li>
                <li>
                  <strong>Payment terms and currency:</strong> Please include how to make payments
                  (for example, advance, net 30 days) and mention the currency name.
                </li>
                <li>
                  <strong>Method of payment:</strong> Give an explanation of the method you plan to use
                  for payment (for example, wire transfer or letter of credit).
                </li>
                <li>
                  <strong>Weight and quantity:</strong> Specify the total and packed weight, amount
                  and how the goods will be packed.
                </li>
                <li>
                  <strong>HS (Harmonized System) code:</strong> Classification codes help to clear
                  customs and work out the duty.
                </li>
                <li>
                  <strong>Incoterms:</strong> Discuss the exact Incoterms as they explain what each
                  party should do about shipping, insurance, and customs.
                </li>
                <li>
                  <strong>Country of origin:</strong> State where the goods were manufactured or
                  produced.
                </li>
                <li>
                  <strong>Transport details:</strong> Include the types of transportation used, the
                  route and the marks on the ship.
                </li>
                <li>
                  <strong>Additional charges:</strong> Any freight, insurance, or certification costs.
                </li>
                <li>
                  <strong>Certification and language:</strong> Some countries require certified invoices
                  or translations—check requirements with your buyer or local chamber of commerce.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Why Use Eximpe's Invoice Generation Tool?
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  <strong>Minimizes errors:</strong> Our automated solution allows us to reduce mistakes
                  and ensure each crucial detail for customs is present.
                </li>
                <li>
                  <strong>Easy to use:</strong> Prepare invoices in the formats, currencies and languages
                  needed by businesses across the globe and local laws.
                </li>
                <li>
                  <strong>Professionalism:</strong> Standardized templates ensure your invoices are
                  polished and consistent, building trust with partners and clients.
                </li>
                <li>
                  <strong>Integration:</strong> Seamlessly connect with your financial systems for easy
                  data sharing and record-keeping.
                </li>
                <li>
                  <strong>Compliance:</strong> Stay up-to-date with international trade regulations, tax
                  calculations, and documentation standards.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Best Practices for Invoice Generation
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  Check twice that everything is correct, and pay special attention to both HS codes and
                  payment arrangements.
                </li>
                <li>
                  Make sure all the information on the invoice is the same as that on the other shipping
                  and banking documents.
                </li>
                <li>
                  Once you are finished writing your email, store a copy for reference before finishing
                  and sending it.
                </li>
                <li>Ensure you upload any necessary files and apply digital signatures to the documents.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Frequently Asked Questions
              </h3>
              <div className="space-y-4 mt-4">
                <div>
                  <p className="font-bold">Can I generate invoices for multiple countries and currencies?</p>
                  <p>
                    Yes, Eximpe's tool supports multicurrency and multilingual invoices to accommodate your
                    global trade needs.
                  </p>
                </div>
                <div>
                  <p className="font-bold">What if I need to update an invoice after creation?</p>
                  <p>
                    You can save invoices as drafts and edit them before finalizing and sending them,
                    ensuring flexibility and accuracy.
                  </p>
                </div>
                <div>
                  <p className="font-bold">Are Eximpe invoices compliant with customs requirements?</p>
                  <p>
                    All our templates are based on international laws and guidelines and include all the
                    required fields for main shipping carriers and organizations. Our support team is happy
                    to give you the information you need and help you create invoices for each transaction.
                  </p>
                </div>
              </div>
            </div>
            {/* /SEO Content Placeholder */}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceGenerator;
